import AccountCircle from '@mui/icons-material/AccountCircle';
import NoAccounts from '@mui/icons-material/NoAccounts';
import Avatar from '@mui/material/Avatar';
import MuiChip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import withStyles from '@mui/styles/withStyles';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import {
  getHighestRole,
  getHighestRoleColor,
  getHighestRoleCompColor,
  getUser,
} from '../../utils/userHelper';
import Chip from '../Chip';
import styles from './styles';

const freeFormUserAccountLabel = 'Not a User';
const freeFormUserAccountText = 'Not associated with a Questri user';

const UserComponent = ({
  user,
  secondary,
  mini,
  classes,
  chip,
  bordered,
  disableGutters,
  showDelete,
  onDelete,
  onClick,
  variant,
  userAction,
  cacheBust,
  size,
  chipSize,
  showAccountIndicator,
  ...rest
}) => {
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);

  const tenantUser = ((user && user.id) || !barnUsersMap)
    ? user
    : getUser(user, barnUsersMap);

  const {
    userFullName = (user && user.userFullName) || 'Questri System',
    avatar,
  } = tenantUser;

  const isFreeFormUser = !!(tenantUser.freeFormUser || tenantUser.freeFormRecentUser);

  const miniAvatar = (
    <Avatar
      src={avatar && `${avatar}${cacheBust
        ? `?cacheBust=${new Date().getTime()}`
        : ''}`}
      className={[
        (!(size && mini)
          ? classes.miniAvatar
          : ''),
        bordered
          ? classes.bordered
          : '',
      ].join(' ')}
      classes={(!(size && mini) && { img: classes.miniImg }) || {}}
      sx={(size && mini) && {
        height: size,
        width: size,
      }}
    />
  );

  if (mini) {
    return miniAvatar;
  }

  if (chip) {
    return (
      <Chip
        avatar={miniAvatar}
        label={userFullName}
        disableGutters={disableGutters}
        showDelete={showDelete}
        onDelete={onDelete}
        onClick={onClick}
        variant={variant}
        size={((chipSize === 'l' || chipSize === 'xl') && 'large') || 'small'}
        {...rest}
      />
    );
  }

  let accountChipLabel = getHighestRole(tenantUser.roles);
  let accountChipColor = getHighestRoleColor(tenantUser.roles);
  let accountChipIconColor = getHighestRoleCompColor(tenantUser.roles);
  if (isFreeFormUser) {
    accountChipLabel = freeFormUserAccountLabel;
    accountChipColor = undefined;
    accountChipIconColor = undefined;
  }
  let noAccountTooltip;
  if (!noAccountTooltip && isFreeFormUser) {
    noAccountTooltip = freeFormUserAccountText;
  } else if (!noAccountTooltip && tenantUser.email) {
    noAccountTooltip = 'User has not logged in or created an account with provided email';
  } else if (!noAccountTooltip) {
    noAccountTooltip = 'No email provided for user, user cannot create an account or login';
  }

  let secondaryText = secondary;
  if (showAccountIndicator && isFreeFormUser) {
    secondaryText = freeFormUserAccountText;
  }

  return (
    <ListItem
      disableGutters
      classes={{ root: classes.listItem }}
      component="div"
    >
      {
        !!avatar
        && <Avatar src={avatar} className={classes.avatar} />
      }
      {
        !avatar
        && (
          <Avatar className={classes.avatar}>
            <Icon>account_circle</Icon>
          </Avatar>
        )
      }
      <ListItemText
        style={{
          wordBreak: 'break-word',
          paddingRight: 0,
          marginRight: 0,
        }}
        primary={(
          <span>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Box>{tenantUser.userFullName}</Box>
              {
                (showAccountIndicator && !userAction && (
                  tenantUser.auth0UserId
                    ? (
                      <Tooltip title="User has account linked to email and can login">
                        <MuiChip color={getHighestRoleColor(tenantUser.roles)} label={getHighestRole(tenantUser.roles)} size="small" icon={<AccountCircle color={getHighestRoleCompColor(tenantUser.roles)} />} />
                      </Tooltip>
                    )
                    : (
                      <Tooltip title={noAccountTooltip}>
                        <MuiChip color={accountChipColor} label={accountChipLabel} size="small" icon={<NoAccounts color={accountChipIconColor} />} />
                      </Tooltip>
                    )
                ))
              }
            </Box>
            {userAction}
          </span>
        )}
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{
          component: 'div',
          width: '100%',
        }}
        secondary={secondaryText}
        classes={{ root: classes.root }}
      />
    </ListItem>
  );
};

UserComponent.propTypes = {
  user: PropTypes.object,
  secondary: PropTypes.string,
  classes: PropTypes.object,
  mini: PropTypes.bool,
  chip: PropTypes.bool,
  bordered: PropTypes.bool,
  disableGutters: PropTypes.bool,
  showDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  variant: PropTypes.string,
  userAction: PropTypes.any,
  cacheBust: PropTypes.bool,
  size: PropTypes.number,
  chipSize: PropTypes.any,
  showAccountIndicator: PropTypes.bool,
};

UserComponent.defaultProps = {
  user: {},
  secondary: '',
  classes: {},
  mini: false,
  chip: false,
  bordered: false,
  disableGutters: false,
  showDelete: false,
  onDelete: () => { },
  onClick: () => { },
  variant: 'default',
  userAction: '',
  cacheBust: false,
  size: undefined,
  chipSize: null,
  showAccountIndicator: false,
};

export default withStyles(styles)(UserComponent);
